import { toast } from '@heroui/react';
import * as React from 'react';
import type { Selection, SortDescriptor } from 'react-aria-components';
import type { SortingState, PaginationState } from '@tanstack/react-table';
import {
  AlertDialog,
  Button,
  Dropdown,
  Label,
  ListBox,
  Pagination,
  SearchField,
  Separator,
  cn,
} from '@heroui/react';
import { DataGrid, InlineSelect } from '@heroui-pro/react';
import type { DataGridColumn } from '@heroui-pro/react';
import { trpc } from '@/lib/trpc';
import { PrizeEditorDialog, PrizeEditorMode } from '@/pages/activities/components/PrizeEditorDialog';
import {
  IconPlus as Plus,
  IconTrash as Trash2,
  IconRefresh as RefreshCw,
  IconDotsVertical as MoreVertical,
} from '@tabler/icons-react';
import { RacDialogTriggerAnchor } from '@/components/RacDialogTriggerAnchor';

const SELECT_COLUMN_WIDTH = 56;

function sortingToDescriptor(sorting: SortingState): SortDescriptor | undefined {
  const s = sorting[0];
  if (!s) return undefined;
  return {
    column: s.id,
    direction: s.desc ? 'descending' : 'ascending',
  };
}

function descriptorToSorting(descriptor: SortDescriptor | undefined): SortingState {
  if (!descriptor?.column) return [];
  return [
    {
      id: String(descriptor.column),
      desc: descriptor.direction === 'descending',
    },
  ];
}

function buildPaginationPages(totalPages: number, safePage: number): (number | 'ellipsis')[] {
  const pages: (number | 'ellipsis')[] = [];
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
  } else {
    pages.push(1);
    if (safePage > 3) pages.push('ellipsis');
    const start = Math.max(2, safePage - 1);
    const end = Math.min(totalPages - 1, safePage + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    if (safePage < totalPages - 2) pages.push('ellipsis');
    pages.push(totalPages);
  }
  return pages;
}

interface PrizesStepContentProps {
  activityId: string;
  onPrizesChanged?: () => void;
}

export function PrizesStepContent({ activityId, onPrizesChanged }: PrizesStepContentProps) {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [pagination, setPagination] = React.useState<PaginationState>({ pageIndex: 0, pageSize: 20 });
  const [selectedKeys, setSelectedKeys] = React.useState<Selection>(new Set());
  const [searchKeyword, setSearchKeyword] = React.useState('');
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const [dialogMode, setDialogMode] = React.useState<PrizeEditorMode>('create');
  const [editingPrize, setEditingPrize] = React.useState<any | null>(null);
  const [isRemoveDialogOpen, setIsRemoveDialogOpen] = React.useState(false);
  const [prizeToRemove, setPrizeToRemove] = React.useState<any | null>(null);

  const sortBy = sorting.length === 0 ? undefined : sorting[0].id;
  const sortOrder = sorting.length === 0 ? undefined : sorting[0].desc ? 'desc' : 'asc';

  const { data, isLoading, refetch } = trpc.activityPrizes.list.useQuery(
    {
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      activityId,
      sortBy: sortBy as any,
      sortOrder: sortOrder as any,
    },
    { enabled: !!activityId }
  );

  type PrizesListData = NonNullable<typeof data>;
  type Prize = PrizesListData['items'][number];
  const prizesRaw = data?.items ?? [];
  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / (pagination.pageSize || 20)));

  const prizes = React.useMemo(() => {
    const k = searchKeyword.trim().toLowerCase();
    if (!k) return prizesRaw;
    return prizesRaw.filter((p) => {
      const level = String(p.level ?? '').toLowerCase();
      const name = String(p.name ?? '').toLowerCase();
      const model = String((p as { model?: string | null }).model ?? '').toLowerCase();
      return level.includes(k) || name.includes(k) || model.includes(k);
    });
  }, [prizesRaw, searchKeyword]);

  const handleEdit = React.useCallback((row: Prize) => {
    setDialogMode('edit');
    setEditingPrize(row);
    setIsDialogOpen(true);
  }, []);

  const handleRemove = React.useCallback((row: Prize) => {
    setPrizeToRemove(row);
    setIsRemoveDialogOpen(true);
  }, []);

  const columns = React.useMemo<DataGridColumn<Prize>[]>(
    () => [
      {
        id: 'level',
        accessorKey: 'level',
        header: '奖品等级',
        allowsSorting: true,
        allowsResizing: true,
        minWidth: 120,
        cell: (row) => <span className="font-medium">{String(row.level || '')}</span>,
      },
      {
        id: 'name',
        accessorKey: 'name',
        header: '奖品名称',
        allowsSorting: true,
        allowsResizing: true,
        minWidth: 180,
        cell: (row) => {
          const model = row.model as string | null | undefined;
          return (
            <div className="flex flex-col gap-1">
              <span>{String(row.name || '')}</span>
              {model ? <span className="text-muted-foreground text-sm">{model}</span> : null}
            </div>
          );
        },
      },
      {
        id: 'value',
        accessorKey: 'value',
        header: '奖品价值',
        allowsSorting: false,
        allowsResizing: true,
        minWidth: 100,
        cell: (row) => {
          const value = row.value as number | null | undefined;
          return <span>{value != null ? `¥${Number(value).toFixed(2)}` : '-'}</span>;
        },
      },
      {
        id: 'quantity',
        accessorKey: 'quantity',
        header: '数量',
        allowsSorting: false,
        allowsResizing: true,
        minWidth: 80,
        cell: (row) => {
          const quantity = row.quantity as number | null | undefined;
          return <span>{quantity != null ? String(quantity) : '-'}</span>;
        },
      },
      {
        id: 'actions',
        header: '',
        align: 'end',
        width: SELECT_COLUMN_WIDTH,
        minWidth: SELECT_COLUMN_WIDTH,
        maxWidth: SELECT_COLUMN_WIDTH,
        allowsSorting: false,
        allowsResizing: false,
        pinned: 'end',
        cell: (prize) => (
          <div className="flex min-w-0 justify-end gap-1 overflow-hidden">
            <Dropdown>
              <Button isIconOnly aria-label="操作" size="sm" variant="ghost">
                <MoreVertical className="size-4" stroke={1.5} />
              </Button>
              <Dropdown.Popover className="min-w-[120px]" placement="bottom end">
                <Dropdown.Menu>
                  <Dropdown.Item id="edit" textValue="编辑" onAction={() => handleEdit(prize)}>
                    <Label>编辑</Label>
                  </Dropdown.Item>
                  <Dropdown.Item
                    id="delete"
                    textValue="删除"
                    variant="danger"
                    onAction={() => handleRemove(prize)}
                  >
                    <Label>删除</Label>
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown.Popover>
            </Dropdown>
          </div>
        ),
      },
    ],
    [handleEdit, handleRemove]
  );

  const columnsWithRowHeader = React.useMemo(() => {
    const first = columns.find((c) => c.id !== 'actions');
    const fh = first?.id;
    const sortable = new Set(['level', 'name']);
    return columns.map((c) => ({
      ...c,
      isRowHeader: c.id === fh,
      allowsSorting: sortable.has(String(c.id)) ? true : (c.allowsSorting ?? false),
    }));
  }, [columns]);

  const sortDescriptor = React.useMemo(() => sortingToDescriptor(sorting), [sorting]);

  const handleSortChange = React.useCallback((d: SortDescriptor) => {
    setSorting(descriptorToSorting(d));
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  }, []);

  const deletePrizeMutation = trpc.activityPrizes.delete.useMutation({
    onSuccess: () => {
      toast.success('奖品已删除');
      setIsRemoveDialogOpen(false);
      setPrizeToRemove(null);
      setSelectedKeys(new Set());
      refetch();
      onPrizesChanged?.();
    },
  });

  const safePage = Math.min(pagination.pageIndex + 1, pageCount);
  const paginationPages = React.useMemo(
    () => buildPaginationPages(pageCount, safePage),
    [pageCount, safePage]
  );

  const pageSizes = [10, 20, 50];

  const dataGridSignature = React.useMemo(() => columnsWithRowHeader.map((c) => c.id).join('\u001f'), [columnsWithRowHeader]);

  return (
    <div className="w-full max-w-5xl space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            onPress={() => {
              setDialogMode('create');
              setEditingPrize(null);
              setIsDialogOpen(true);
            }}
          >
            <Plus className="size-4" stroke={1.5} />
            创建
          </Button>
          <Button variant="danger" isDisabled>
            <Trash2 className="size-4" stroke={1.5} />
            批量删除
          </Button>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <SearchField
            aria-label="搜索奖品"
            value={searchKeyword}
            onChange={(v) => {
              setSearchKeyword(v);
            }}
          >
            <SearchField.Group>
              <SearchField.SearchIcon />
              <SearchField.Input className="w-[min(100%,200px)] min-w-[140px]" placeholder="搜索等级、名称…" />
              <SearchField.ClearButton />
            </SearchField.Group>
          </SearchField>
          <Button isIconOnly variant="secondary" aria-label="刷新" onPress={() => void refetch()}>
            <RefreshCw className="size-4" stroke={1.5} />
          </Button>
        </div>
      </div>

      <div className="relative grid min-h-[320px] min-w-0 grid-rows-[minmax(0,1fr)_auto] gap-3 overflow-hidden rounded-lg border border-divider">
        <div className="relative min-h-0 min-w-0 overflow-hidden">
          <div className="absolute inset-0 flex min-h-0 min-w-0 flex-col">
            {isLoading ? (
              <div className="bg-background/60 absolute inset-0 z-10 flex items-center justify-center text-sm">
                加载中…
              </div>
            ) : null}
            <DataGrid<Prize>
              key={dataGridSignature}
              allowsColumnResize
              aria-label="奖品列表"
              className="flex h-full min-h-0 min-w-0 flex-col !overflow-visible"
              columns={columnsWithRowHeader}
              contentClassName="min-w-0 !overflow-visible"
              data={prizes}
              getRowId={(item) => String(item.id ?? '')}
              renderEmptyState={() => (
                <div className="text-muted-foreground py-8 text-center text-sm">暂无奖品</div>
              )}
              scrollContainerClassName={cn(
                'h-full max-h-[min(480px,55vh)] min-h-0 flex-1 overflow-y-auto overflow-x-auto overscroll-contain',
                '[&_[data-slot=table-resizable-container]]:overflow-visible',
                '[&_[data-slot=table-content]]:!overflow-visible',
                '[&_thead_th]:bg-[var(--surface-secondary)] [&_thead_th]:sticky [&_thead_th]:top-0 [&_thead_th]:z-20',
                '[&_th]:min-w-0 [&_td]:min-w-0',
                '[&_[role=columnheader]]:min-w-0 [&_[role=gridcell]]:min-w-0'
              )}
              selectedKeys={selectedKeys}
              selectionMode="multiple"
              showSelectionCheckboxes
              sortDescriptor={sortDescriptor}
              variant="primary"
              onSelectionChange={setSelectedKeys}
              onSortChange={handleSortChange}
            />
          </div>
        </div>

        <div
          className="text-muted-foreground flex min-w-0 shrink-0 flex-nowrap items-center justify-between gap-3 overflow-x-auto border-t border-divider px-2 py-2 text-sm"
          data-slot="prizes-pagination"
        >
          <div className="flex w-fit shrink-0 items-center">
            <Pagination className="w-fit max-w-none" size="sm">
              <Pagination.Content className="flex w-fit min-w-0">
                <Pagination.Item>
                  <Pagination.Previous
                    isDisabled={safePage <= 1}
                    onPress={() =>
                      setPagination((p) => ({
                        ...p,
                        pageIndex: Math.max(0, p.pageIndex - 1),
                      }))
                    }
                  >
                    <Pagination.PreviousIcon />
                  </Pagination.Previous>
                </Pagination.Item>
                {paginationPages.map((p, i) =>
                  p === 'ellipsis' ? (
                    <Pagination.Item key={`e-${i}`}>
                      <Pagination.Ellipsis />
                    </Pagination.Item>
                  ) : (
                    <Pagination.Item key={p}>
                      <Pagination.Link
                        isActive={p === safePage}
                        onPress={() =>
                          setPagination((prev) => ({
                            ...prev,
                            pageIndex: (p as number) - 1,
                          }))
                        }
                      >
                        {p}
                      </Pagination.Link>
                    </Pagination.Item>
                  )
                )}
                <Pagination.Item>
                  <Pagination.Next
                    isDisabled={safePage >= pageCount}
                    onPress={() =>
                      setPagination((p) => ({
                        ...p,
                        pageIndex: Math.min(pageCount - 1, p.pageIndex + 1),
                      }))
                    }
                  >
                    <Pagination.NextIcon />
                  </Pagination.Next>
                </Pagination.Item>
              </Pagination.Content>
            </Pagination>
          </div>

          <div className="flex h-4 w-fit shrink-0 flex-nowrap items-center gap-3 whitespace-nowrap">
            <InlineSelect
              aria-label="每页条数"
              value={String(pagination.pageSize)}
              onChange={(v) => {
                if (v) {
                  setPagination((p) => ({
                    ...p,
                    pageSize: Number(v),
                    pageIndex: 0,
                  }));
                }
              }}
            >
              <InlineSelect.Trigger>
                <span className="text-muted">每页</span>
                <InlineSelect.Value />
                <InlineSelect.Indicator />
              </InlineSelect.Trigger>
              <InlineSelect.Popover className="w-[88px]">
                <ListBox>
                  {pageSizes.map((n) => (
                    <ListBox.Item key={n} id={String(n)} textValue={String(n)}>
                      {n}
                      <ListBox.ItemIndicator />
                    </ListBox.Item>
                  ))}
                </ListBox>
              </InlineSelect.Popover>
            </InlineSelect>
            <Separator className="!h-4 shrink-0" orientation="vertical" />
            <span className="text-muted shrink-0">
              第 {total === 0 ? 0 : (safePage - 1) * pagination.pageSize + 1}–
              {Math.min(safePage * pagination.pageSize, total)} 条，共 {total} 条
            </span>
          </div>
        </div>
      </div>

      <PrizeEditorDialog
        open={isDialogOpen}
        mode={dialogMode}
        prizeData={editingPrize}
        activityId={activityId}
        onOpenChange={setIsDialogOpen}
        onSuccess={() => {
          refetch();
          onPrizesChanged?.();
        }}
      />

      <AlertDialog isOpen={isRemoveDialogOpen} onOpenChange={setIsRemoveDialogOpen}>
        <RacDialogTriggerAnchor />
        <AlertDialog.Backdrop>
          <AlertDialog.Container placement="center">
            <AlertDialog.Dialog>
              <AlertDialog.Header>
                <AlertDialog.Heading>确认删除</AlertDialog.Heading>
              </AlertDialog.Header>
              <AlertDialog.Body>
                确定要删除奖品「{prizeToRemove?.name}」吗？删除后不可恢复。
              </AlertDialog.Body>
              <AlertDialog.Footer>
                <Button
                  variant="secondary"
                  onPress={() => {
                    setIsRemoveDialogOpen(false);
                    setPrizeToRemove(null);
                  }}
                >
                  取消
                </Button>
                <Button
                  variant="danger"
                  isDisabled={deletePrizeMutation.isPending}
                  onPress={() => prizeToRemove?.id && deletePrizeMutation.mutate({ id: prizeToRemove.id })}
                >
                  {deletePrizeMutation.isPending ? '删除中…' : '确认删除'}
                </Button>
              </AlertDialog.Footer>
            </AlertDialog.Dialog>
          </AlertDialog.Container>
        </AlertDialog.Backdrop>
      </AlertDialog>
    </div>
  );
}
